import { useAtomValue } from 'jotai/index';
import React, { useCallback, useMemo, useState } from 'react';

import { HorizontalContainer, Modal, VerticalContainer } from '@/components';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import { AdolescentPatientWarning } from '@/caregiverSpace/entities/patient';

import { shouldWarnAboutStart } from '../lib/shouldWarnAboutStart';
import { useCaregiverVideoCallConfirmation } from '../lib/useCaregiverVideoCallConfirmation';
import { isHandledByOtherAtom } from '../model/store';
import { EarlyJoinWarningView } from './EarlyJoinWarningView';
import { HandledByOtherView } from './HandledByOtherView';
import { InfoTable } from './InfoTable';
import { VideoCallButton } from './VideoCallButton';

type VideoCallConfirmationViewProps = {
    consultationUid: string;
    onClose: (isAutoClosed?: boolean) => void;
};

const VALID_VERIFICATION_TEXTS = ['yes', 'ja'];

export const VideoCallConfirmationView = ({
    consultationUid,
    onClose,
}: VideoCallConfirmationViewProps) => {
    const [verificationText, setVerificationText] = useState('');
    const [showEarlyJoinWarning, setShowEarlyJoinWarning] = useState(false);

    const { confirmation, makeCall: onMakeCall } =
        useCaregiverVideoCallConfirmation({
            consultationUid,
            onClose,
        });

    const isHandledByOther = useAtomValue(isHandledByOtherAtom);

    const isVerificationTextValid = useMemo(
        () =>
            VALID_VERIFICATION_TEXTS.includes(
                verificationText.trim().toLowerCase(),
            ),
        [verificationText],
    );

    const isButtonDisabled = isHandledByOther && !isVerificationTextValid;

    const isTooEarly = shouldWarnAboutStart(
        confirmation?.Consultation.ScheduledOn,
    );

    const proceedWithCall = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (!isButtonDisabled) {
                onMakeCall();
                onClose(true);
            }
        },
        [isButtonDisabled, onMakeCall, onClose],
    );

    const handleCallClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (isTooEarly) {
                setShowEarlyJoinWarning(true);
                return;
            }
            proceedWithCall(e);
        },
        [isTooEarly, proceedWithCall],
    );

    return (
        <VerticalContainer>
            <InfoTable
                fields={confirmation?.FormattedFields}
                shouldWarnAboutStart={isTooEarly}
            />
            <AdolescentPatientWarning
                dateOfBirth={confirmation?.Patient.DateOfBirth}
            />
            {isHandledByOther && (
                <HandledByOtherView
                    verificationText={verificationText}
                    setVerificationText={setVerificationText}
                />
            )}
            <HorizontalContainer className="justify-end">
                <VideoCallButton
                    onClick={handleCallClick}
                    disabled={isButtonDisabled}
                />
            </HorizontalContainer>
            {showEarlyJoinWarning && (
                <Modal
                    title={R.EarlyJoinWarningTitle}
                    onClose={() => setShowEarlyJoinWarning(false)}
                >
                    <EarlyJoinWarningView
                        onConfirm={proceedWithCall}
                        onCancel={() => setShowEarlyJoinWarning(false)}
                    />
                </Modal>
            )}
        </VerticalContainer>
    );
};
